import { FC } from 'react';

import { clearErrors } from 'store/errorSlice';

import { useAppDispatch } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';

import { RaaDialog } from 'raa-react';

import { HTTP_STATUS, ModelError } from 'models';

const BackendMessageDialog: FC<{ error: ModelError | undefined }> = ({ error }) => {
  const dispatch = useAppDispatch();
  const { clearDialogStack } = useDialog();
  const isUnprocessedError = error?.status === HTTP_STATUS.UNPROCESSABLE_ENTITY;

  return (
    <RaaDialog
      header={isUnprocessedError ? 'Kan inte utföras' : 'Ett fel har uppstått'}
      cancel={() => {
        dispatch(clearErrors());
        clearDialogStack();
      }}
      cancelButtonText={isUnprocessedError ? 'stäng' : 'avbryt'}
      proceed={isUnprocessedError ? undefined : () => window.location.reload()}
      proceedButtonText="Ladda om sidan"
      ariaDescribedBy="dialog-description"
    >
      {error?.error_fields ? (
        <>
          <p id="dialog-description" className="tw-my-4">
            Följande problem uppstod när sidan skulle laddas:
          </p>

          <ul className={'tw-space-y-4'}>
            {Object.entries(error.error_fields).map(([fieldKey, errorFields]) => (
              <li key={fieldKey}>
                <div className={'tw-font-semibold tw-capitalize'}>{fieldKey.split('.').pop()}</div>
                <ul>
                  {errorFields.map((errorField, index) => {
                    return (
                      <li key={errorField.field + '_' + index}>
                        <RejectedValueInfo rejectedValue={errorField.rejected_value} fieldName={fieldKey} />
                        <span className={'tw-italic'}>({errorField.message})</span>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        </>
      ) : error?.message ? (
        <p id="dialog-description" className="tw-mb-4 tw-ml-2 tw-mt-10">
          {error.message}
        </p>
      ) : (
        <p id="dialog-description" className="tw-mb-4 tw-mt-10">
          Felmeddelande saknas
        </p>
      )}
    </RaaDialog>
  );
};

const isStringArray = (a: any): a is string[] => Array.isArray(a) && a.every((v) => typeof v === 'string');

const RejectedValueInfo: FC<{ rejectedValue: string | string[] | object | object[]; fieldName: string }> = ({
  rejectedValue,
  fieldName
}) => {
  if (typeof rejectedValue === 'string') {
    return <span className={'tw-ml-4 tw-flex tw-max-w-full'}>Felaktigt värde: {rejectedValue}</span>;
  } else if (isStringArray(rejectedValue)) {
    return (
      <div>
        <span className={'tw-ml-4 tw-flex tw-max-w-full'}>Felaktiga värden:</span>
        {rejectedValue.map((rejectedValueItem) => (
          <span key={fieldName + '_' + rejectedValueItem} className={'tw-ml-8 tw-flex tw-max-w-full'}>
            {rejectedValueItem}
          </span>
        ))}
      </div>
    );
  }
};

export default BackendMessageDialog;
